import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { inviteOrReactivateUserSchema } from "../../../../shared/mutators/inviteOrReactivateUser";
import type { queries } from "../../../../shared/queries";
import {
  type GrantEntry,
  type RoleOption,
  rolesForScope,
} from "../../../../shared/rbac/roles";
import { customRolesForScope } from "../../../lib/grantAssembly";
import { defaultRoleForScope } from "../../../lib/defaultRoleForScope";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { usePrincipalModals } from "../../../hooks/usePrincipalModals";
import { useScopeAccessCloseWarning } from "../../ScopeAccessCloseWarning";
import { ScopeAccessTable } from "../../ScopeAccessTable";
import {
  HeaderIconContainer,
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../../SettingsLayout";
import { Text } from "../../Text";
import {
  Card,
  FieldLabel,
  GROUP_TOOLTIP_COPY,
  HelpTooltip,
  SectionHeading,
  settingsModalStyle,
} from "../shared";

type UserGroup = QueryRowType<ReturnType<typeof queries.accessGraphGroups>>;
type CustomRole = QueryRowType<ReturnType<typeof queries.allRolesLite>>;

export function InviteMemberModal({
  open,
  onClose,
  groups,
  customRoles,
}: {
  open: boolean;
  onClose: () => void;
  groups: readonly UserGroup[];
  customRoles: readonly CustomRole[];
}) {
  const z = useZero();
  const { onOpenGroup } = usePrincipalModals();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [groupGrants, setGroupGrants] = useState<GrantEntry[]>([]);
  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({ singular: "group", plural: "groups" });

  const handleClose = () => {
    setInviteEmail("");
    setInviteBusy(false);
    setGroupGrants([]);
    onClose();
  };

  const emailParse =
    inviteOrReactivateUserSchema.shape.email.safeParse(inviteEmail);

  const groupCustomRoles = useMemo(
    () => customRolesForScope(customRoles, "user_group"),
    [customRoles]
  );
  const groupRoleOptions: RoleOption[] = useMemo(
    () => rolesForScope("user_group", groupCustomRoles),
    [groupCustomRoles]
  );

  const assignedGroups = useMemo(() => {
    const ids = new Set(
      groupGrants
        .map((g) => g.userGroupId)
        .filter((v): v is string => Boolean(v))
    );
    return groups.filter((g) => ids.has(g.id));
  }, [groups, groupGrants]);

  const removeGroup = useCallback(
    (groupId: string) =>
      setGroupGrants((prev) => prev.filter((g) => g.userGroupId !== groupId)),
    []
  );

  const handleInviteUser = async () => {
    if (!emailParse.success) return;
    setInviteBusy(true);
    try {
      const grants =
        groupGrants.length > 0
          ? groupGrants
              .filter((g): g is GrantEntry & { userGroupId: string } =>
                Boolean(g.userGroupId)
              )
              .map((g) => ({
                userGroupId: g.userGroupId,
                builtinRoleId: g.builtinRoleId,
                customRoleId: g.customRoleId,
              }))
          : undefined;
      await z.mutate(
        sharedMutators.inviteOrReactivateUser({
          id: nanoid(12),
          email: emailParse.data,
          groupGrants: grants,
        })
      );
      handleClose();
    } finally {
      setInviteBusy(false);
    }
  };

  const close = () => requestClose(handleClose);
  const sendInvite = () => requestClose(() => void handleInviteUser());

  return (
    <>
      <Modal open={open} onClose={close} style={settingsModalStyle}>
        <SettingsModalHeader>
          <HeaderIconContainer>
            <Icon name="user-round-plus" size={16} />
          </HeaderIconContainer>
          <Text variant="text-regular-md">Invite member</Text>
          <HeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </HeaderRight>
        </SettingsModalHeader>

        <SettingsModalBody style={{ gap: 24 }} data-popover-host>
          <Card>
            <FieldLabel>
              <Box horizontal={8} bottom={10}>
                <Text variant="text-small-md">Email</Text>
              </Box>
              <Input
                autoFocus
                type="email"
                placeholder="name@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendInvite();
                }}
                style={{ width: "100%" }}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  We’ll email them a sign-in link.
                </Text>
              </Box>
            </FieldLabel>
          </Card>

          {groups.length > 0 && (
            <Card>
              <SectionHeading>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Text variant="text-regular-md">Groups</Text>
                  <HelpTooltip content={GROUP_TOOLTIP_COPY} />
                </div>
                <Text variant="text-small" color="var(--text-secondary)">
                  Choose which groups they will join.
                </Text>
              </SectionHeading>
              <ScopeAccessTable
                allGrants={groupGrants}
                assignedGroups={assignedGroups}
                assignedUsers={[]}
                roleOptions={groupRoleOptions}
                allGroups={groups}
                allUsers={[]}
                customRoles={groupCustomRoles}
                canManage
                compact
                commitGrants={(next) => setGroupGrants(next)}
                removeGroup={removeGroup}
                removeUser={() => {}}
                searchPlaceholder="Add groups…"
                onOpenGroup={onOpenGroup}
                stagedGrantsRef={stagedGrantsRef}
                defaultRole={defaultRoleForScope(
                  "user_group",
                  groupRoleOptions
                )}
              />
            </Card>
          )}
        </SettingsModalBody>

        <SettingsModalFooter style={{ gap: 8 }}>
          <Button size="l" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            size="l"
            onClick={sendInvite}
            disabled={!emailParse.success || inviteBusy}
          >
            Send invite
          </Button>
        </SettingsModalFooter>
      </Modal>
      {closeWarningModal}
    </>
  );
}
